import React, { useState } from 'react';
import { connect } from 'react-redux';
import { ToolbarItem } from '@amzn/aac-chrome-internal-api';
import {
  Divider,
  Flex,
  ThemeMode,
  useIsMobile,
  useThemeMode,
} from '@amzn/storm-ui';
import { RootState } from 'src/app/store';
import {
  SelectedToolbarItem,
  selectLogoImage,
  selectSelectedToolbarItem,
  selectToolbarItems,
  selectUserInfoFromNav,
  ToolbarItemProps,
} from 'src/features/nav/store/slice';
import { PAGE_LAYOUT_TYPE, toggleMobileSideNav } from 'src/app/slice';
import {
  MobileLogo,
  MobileLogoWrapper,
  MobileMenuCloseBtn,
  MobileNavWrapper,
} from './styles';
import MobileSideNavItem from './MobileSideNavItem';
import UserPreferenceView from './UserPreferenceView';
import { ImageProps } from 'src/commons/utils/types';
import { UserInfo } from 'src/features/header/store/slice';
import { UserDropdownPublisher } from 'src/commons/utils/metrics';
import { MobileNavSheet } from './MobileNavSheet';
import { isStandardPageLayoutType } from 'src/commons/utils/helpers';
import {
  staticDarkImgSrc,
  staticDarkImgSrcWithCache,
  staticImgSrc,
  staticImgSrcWithCache,
} from 'src/commons/constants';
import { UserPreferenceMenuItem } from './UserPreferenceMenuItem';
import {
  PageHeaderBundleProvider,
  useSidenavBundle,
} from 'src/providers/PantherLocalizationProvider';
import { isFromBtswWc, isFromTrim } from 'src/commons/utils';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { ExternalToggle } from 'src/features/header/components/ExternalToggle/ExternalToggle';
import { ExternalSwitchWrapper } from 'src/features/header/components/UtilityMenu/styles';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';

interface MobileSideNavProps {
  logoImage: ImageProps;
  toolbarItems: Array<ToolbarItem>;
  userInfo: UserInfo;
  selectedToolbarItem?: SelectedToolbarItem;
  mobileMenuCloseBtnHandler: (...args: any[]) => any;
}

export const MobileSideNav: React.VFC<MobileSideNavProps> = ({
  logoImage,
  toolbarItems,
  userInfo,
  selectedToolbarItem,
  mobileMenuCloseBtnHandler,
}) => {
  const { themeMode } = useThemeMode();
  const isMobile = useIsMobile();
  const { pageLayoutType } = useChromeConfigContext();
  const isStandardLayout = isStandardPageLayoutType(pageLayoutType);
  const bundle = useSidenavBundle();
  const [isUserPreferenceOpen, setIsUserPreferenceOpen] = useState(false);
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const { isInternalView } = internalChromeConfig;

  const getLogoSrc = () => {
    const useDarkLogo = themeMode === ThemeMode.LIGHT && isMobile;
    if (isFromTrim() || isFromBtswWc()) {
      return useDarkLogo ? staticDarkImgSrcWithCache : staticImgSrcWithCache;
    }
    return useDarkLogo
      ? staticDarkImgSrcWithCache
      : (logoImage?.src ?? staticImgSrcWithCache);
  };

  const handleError = (e) => {
    e.stopPropagation();
    e.target.src =
      themeMode === ThemeMode.LIGHT && isMobile
        ? staticDarkImgSrc
        : staticImgSrc;
  };

  const sideNavItems = toolbarItems.map((item) => (
    <MobileSideNavItem key={item.id} {...item} />
  ));

  const firstAccessibleApp = (toolbarItems[0] as ToolbarItemProps)
    ?.sidebarGroups[0]?.links[0]?.link?.url;

  const handleUserPreferencesClick = () => {
    UserDropdownPublisher.publishCounterMonitor('Open', 1);
    setIsUserPreferenceOpen(true);
  };

  const disableExternalToggle = (
    selectedToolbarItem
      ? !selectedToolbarItem.shouldDisplayExternalToggle
      : isInternalView && pageLayoutType === PAGE_LAYOUT_TYPE.INTERNAL_STANDARD
  ) as boolean;

  return (
    <MobileNavWrapper>
      <Flex alignItems="center" padding="xsmall none">
        <MobileMenuCloseBtn
          onClick={mobileMenuCloseBtnHandler}
          label={bundle.getMessage('sidenav-close-menu-aria-label')}
          data-ccx-e2e-id="aac-mobile-sidenav-close-btn"
          {...getTaktAttributes(
            'aac-mobile-sidenav-close-btn',
            chromeComponents.NAV,
          )}
        />
        <MobileLogoWrapper href={firstAccessibleApp}>
          <MobileLogo
            src={getLogoSrc()}
            alt={bundle.getMessage('sidenav-logo-text')}
            onError={handleError}
          />
        </MobileLogoWrapper>
      </Flex>
      {isStandardLayout && (
        <>
          {isInternalView && (
            <PageHeaderBundleProvider>
              <ExternalSwitchWrapper>
                <ExternalToggle isDisabled={disableExternalToggle} />
              </ExternalSwitchWrapper>
            </PageHeaderBundleProvider>
          )}
          {sideNavItems}
        </>
      )}
      <>
        <Divider />
        <UserPreferenceMenuItem handleClick={handleUserPreferencesClick} />
      </>
      <MobileNavSheet
        isOpen={isUserPreferenceOpen}
        closeButton={{
          type: 'icon',
          label: 'close',
        }}
        type="light"
        onClose={() => {
          UserDropdownPublisher.publishCounterMonitor('Close', 1);
          setIsUserPreferenceOpen(false);
        }}
        side="start"
        padding="none"
        hasInnerPadding={false}
      >
        <UserPreferenceView {...userInfo} />
      </MobileNavSheet>
    </MobileNavWrapper>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    logoImage: selectLogoImage(state),
    toolbarItems: selectToolbarItems(state),
    userInfo: selectUserInfoFromNav(state),
    selectedToolbarItem: selectSelectedToolbarItem(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    mobileMenuCloseBtnHandler: () => dispatch(toggleMobileSideNav()),
  };
};

export const ConnectedMobileSideNav = connect(
  mapStateToProps,
  mapDispatchToProps,
)(MobileSideNav);
